/**
 * The Support Inbox pages — every fact they show, and every call they make.
 *
 * Design docs/superpowers/specs/2026-09-26-support-inbox-design.md, section 6.
 * The endpoints are section 5, already on this branch:
 * `backend/src/routes/admin-support-inbox.ts` is the contract, and the shapes
 * below were copied from its exported interfaces field by field, never guessed.
 *
 * ── WHY THE LOGIC LIVES IN A `.ts` FILE AND NOT IN THE COMPONENTS ──────────
 * Root jest compiles no `.tsx` (its ts-jest config sets no `jsx` option, and
 * a `.tsx` import fails with `SyntaxError: Unexpected token '<'`). So every
 * rule lives here or in the two hooks, where a test can EXECUTE it, and the
 * `.tsx` files hold only markup. Same split as the notifications page.
 *
 * ── NO `lucide-react`, NO `@/lib/utils` IN THIS FILE ───────────────────────
 * Both live ONLY in `frontend/admin/node_modules`, which a root jest run is
 * not guaranteed to have. The `.tsx` files import them; this file and the
 * hooks do not.
 *
 * ── TEXT ONLY ──────────────────────────────────────────────────────────────
 * The panel has no sanitizer and a CSP of `default-src 'none'` with no
 * `frame-src` (design section 0). A message body reaches the screen ONLY as
 * `text_body`, rendered as a React text child. When the message also carried
 * HTML (`html_present`), the page says so and offers the raw `.eml` through a
 * presigned URL opened in a new tab — it never renders it.
 */
import { adminFetch, AdminApiError } from '@/providers/admin-api';

/* ═════════════════════════════════════════════════════════════════════════
 * Paths
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * Quoted literals on purpose. `tests/unit/lint/admin-fetch-route-exists.test.ts`
 * scans this tree for `'/v1/admin/...'` strings and resolves each against
 * `backend/src/index.ts`, where the whole family is declared behind
 * `path.startsWith('/v1/admin/support-inbox/')` plus inline id regexes. A URL
 * built from a runtime-joined base carries no quote before the slash and the
 * lint never sees it, so every path here is a literal prefix plus an id.
 */
export const SUPPORT_INBOX_PATHS = {
  summary: '/v1/admin/support-inbox/summary',
  threads: '/v1/admin/support-inbox/threads',
  thread: (threadId: string) => `/v1/admin/support-inbox/threads/${threadId}`,
  reply: (threadId: string) => `/v1/admin/support-inbox/threads/${threadId}/reply`,
  attachmentDownload: (attachmentId: string) =>
    `/v1/admin/support-inbox/attachments/${attachmentId}/download`,
  raw: (messageId: string) => `/v1/admin/support-inbox/messages/${messageId}/raw`,
  /** The admins list the assignee select reads (gated by `admin.list` server-side). */
  admins: '/v1/admin/admins',
} as const;

/* ═════════════════════════════════════════════════════════════════════════
 * Permissions — the keys migration 349 seeds
 * ═════════════════════════════════════════════════════════════════════════ */

export const READ_PERMISSION = 'support_inbox.read';
export const WRITE_PERMISSION = 'support_inbox.write';
export const READ_RESTRICTED_PERMISSION = 'support_inbox.read_restricted';
export const DELETE_PERMISSION = 'support_inbox.delete';
/** `GET /v1/admin/admins` is gated by THIS key, not by a support-inbox one. */
export const ADMIN_LIST_PERMISSION = 'admin.list';

/* ═════════════════════════════════════════════════════════════════════════
 * Shapes — copied from backend/src/routes/admin-support-inbox.ts
 * ═════════════════════════════════════════════════════════════════════════ */

export const THREAD_STATUSES = ['open', 'pending', 'closed', 'spam'] as const;
export type ThreadStatus = (typeof THREAD_STATUSES)[number];

/** `MailboxGroup` in backend/src/support-inbox/domains.ts. */
export type MailboxGroup =
  | 'support'
  | 'hello'
  | 'app'
  | 'privacy-legal'
  | 'partners'
  | 'machine'
  | 'restricted'
  | 'other';

export interface SupportMemberCard {
  id: string;
  email: string | null;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
}

export interface SupportVerdicts {
  spam: string | null;
  virus: string | null;
  spf: string | null;
  dkim: string | null;
  dmarc: string | null;
}

export interface SupportThreadRow {
  id: string;
  mailbox: string;
  group: MailboxGroup;
  inbound_domain: string;
  recipients: string[];
  subject: string | null;
  status: ThreadStatus;
  is_machine: boolean;
  virus_flagged: boolean;
  sender_verified: boolean;
  member_user_id: string | null;
  member: SupportMemberCard | null;
  assigned_admin_id: string | null;
  assigned_admin_display_name: string | null;
  first_from_address: string;
  first_from_name: string | null;
  message_count: number;
  last_message_at: string;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  created_at: string;
  updated_at: string;
  verdicts: SupportVerdicts | null;
}

export type SupportDeliveryState = 'delivered' | 'bounced' | 'complained' | 'sent' | 'unsent';

export interface SupportMessageRow {
  id: string;
  direction: 'inbound' | 'outbound';
  ses_message_id: string | null;
  rfc_message_id: string | null;
  in_reply_to: string | null;
  references_ids: string[];
  envelope_from: string | null;
  from_address: string;
  from_name: string | null;
  to_addresses: string[];
  cc_addresses: string[];
  subject: string | null;
  text_body: string | null;
  text_truncated: boolean;
  html_present: boolean;
  parse_error: string | null;
  has_raw: boolean;
  verdicts: SupportVerdicts;
  headers_truncated: boolean;
  is_machine: boolean;
  sent_by_admin_id: string | null;
  occurred_at: string;
  /** Outbound rows only: SES's verdict from core.email_events, or `sent` while none has arrived. */
  delivery: SupportDeliveryState | null;
  /**
   * Inbound rows only: OUR OWN reply, received back through the inbox (its
   * Message-ID is the SES id of an outbound row in the same thread). Rendered
   * as one muted line, never as a message card, and never a reply target.
   */
  own_copy: boolean;
}

export interface SupportAttachmentRow {
  id: string;
  message_id: string;
  filename: string | null;
  content_type: string;
  size_bytes: number;
  content_id: string | null;
  is_inline: boolean;
  /** True only when the message passed the virus scan AND the bytes were stored. */
  download_allowed: boolean;
}

export interface SupportInboxSummaryData {
  open_by_group: Record<string, number>;
  open_total: number;
  pending_total: number;
  spam: number;
  machine: number;
  assigned_to_me: number;
  /** Open human threads the sender wrote last, waiting more than 24 hours (the sweep's gauge, same number). */
  unanswered_24h: number;
  /**
   * The restricted twin: the same count over ONLY the restricted mailboxes.
   * A number for a holder of `support_inbox.read_restricted`, null for
   * everyone else (the server withholds it; the page shows nothing).
   */
  unanswered_restricted_24h: number | null;
}

export interface SupportThreadListData {
  threads: SupportThreadRow[];
  total: number;
  limit: number;
  offset: number;
  page: number;
  sort_by: string;
  sort_order: 'ASC' | 'DESC';
}

export interface SupportThreadData {
  thread: SupportThreadRow;
  messages: SupportMessageRow[];
  attachments: SupportAttachmentRow[];
  member: SupportMemberCard | null;
  /** The last HUMAN inbound message's from_address — the ONLY address a reply goes to. */
  reply_to_address: string | null;
  can_reply: boolean;
}

export interface SupportReplyData {
  message_id: string;
  ses_message_id: string;
  to: string;
  thread_status: ThreadStatus;
  /** The thread's last_message_at AFTER the reply, as the database wrote it — the next reply's collision-guard key (B5). */
  last_message_at: string;
}

export interface SupportThreadErasedData {
  thread_id: string;
  messages_deleted: number;
  attachments_deleted: number;
  objects_deleted: number;
  objects_failed: number;
}

export interface SupportDownloadData {
  url: string;
  expires_in_seconds: number;
  filename: string;
  content_type: string;
}

/** `AdminRow` in backend/src/routes/admin-management.ts — the fields the select needs. */
export interface AdminListRow {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
}

/* ═════════════════════════════════════════════════════════════════════════
 * Tabs, filters, labels
 * ═════════════════════════════════════════════════════════════════════════ */

/**
 * Personal mailboxes of named people (owner, 2026-09-26: manny@ and max@ are
 * real mailboxes). `PEOPLE_MAILBOXES` in backend/src/support-inbox/domains.ts.
 * Each is a tab of its own; the tab reads by EXACT mailbox (`mailbox=<name>`),
 * not by group, and the server keeps them behind `support_inbox.read_restricted`.
 */
export const PEOPLE_MAILBOXES = ['manny', 'max'] as const;
export type PeopleMailbox = (typeof PEOPLE_MAILBOXES)[number];
export function isPeopleTab(tab: string): tab is PeopleMailbox {
  return (PEOPLE_MAILBOXES as readonly string[]).includes(tab);
}

/** The list's `group` query values — `SUPPORT_INBOX_GROUPS` in the route file — plus the people tabs. */
export type GroupTab = 'all' | MailboxGroup | 'spam' | PeopleMailbox;

export interface GroupTabView {
  key: GroupTab;
  label: string;
}

/**
 * Tab order per design section 6.3. `Restricted` is appended ONLY for an
 * admin holding `support_inbox.read_restricted`: the backend excludes those
 * mailboxes from every read for everyone else, so the tab would always be
 * empty and would name a thing the admin is not meant to see.
 */
const GROUP_TABS_BASE: readonly GroupTabView[] = [
  { key: 'all', label: 'All' },
  { key: 'support', label: 'Support' },
  { key: 'hello', label: 'Hello' },
  { key: 'app', label: 'App' },
  { key: 'privacy-legal', label: 'Privacy & Legal' },
  { key: 'partners', label: 'Partners' },
  { key: 'other', label: 'Other' },
  { key: 'machine', label: 'Machine' },
  { key: 'spam', label: 'Spam' },
];

const RESTRICTED_TAB: GroupTabView = { key: 'restricted', label: 'Restricted' };

/** One tab per person, after Restricted: personal mail is restricted mail, so the same key gates them. */
const PEOPLE_TABS: readonly GroupTabView[] = PEOPLE_MAILBOXES.map((name) => ({
  key: name,
  label: name.charAt(0).toUpperCase() + name.slice(1),
}));

export function visibleGroupTabs(canReadRestricted: boolean): GroupTabView[] {
  return canReadRestricted ? [...GROUP_TABS_BASE, RESTRICTED_TAB, ...PEOPLE_TABS] : [...GROUP_TABS_BASE];
}

/**
 * The number on a tab, from the summary and nothing else. `All`, the mailbox
 * groups, `Other` and `Restricted` count OPEN threads; `Machine` and `Spam`
 * count every thread they hold, because those two are closed by construction
 * and an "open" count would read 0 forever.
 */
export function groupCount(summary: SupportInboxSummaryData | null, tab: GroupTab): number | null {
  if (!summary) return null;
  if (isPeopleTab(tab)) return null; // the summary counts groups, not single mailboxes
  if (tab === 'all') return summary.open_total;
  if (tab === 'machine') return summary.machine;
  if (tab === 'spam') return summary.spam;
  const n = summary.open_by_group[tab];
  return typeof n === 'number' ? n : 0;
}

export const GROUP_LABELS: Record<MailboxGroup, string> = {
  support: 'Support',
  hello: 'Hello',
  app: 'App',
  'privacy-legal': 'Privacy & Legal',
  partners: 'Partners',
  machine: 'Machine',
  restricted: 'Restricted',
  other: 'Other',
};

export const STATUS_LABELS: Record<ThreadStatus, string> = {
  open: 'Open',
  pending: 'Pending',
  closed: 'Closed',
  spam: 'Spam',
};

/** The status pill — `bg-<c>-100 text-<c>-700` with its dark pair, the panel idiom. */
export const STATUS_PILL_CLASSES: Record<ThreadStatus, string> = {
  open: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
  closed: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  spam: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
};

export const DELIVERY_LABELS: Record<SupportDeliveryState, string> = {
  delivered: 'Delivered',
  bounced: 'Bounced',
  complained: 'Complaint',
  sent: 'Sent',
  unsent: 'Unsent',
};

export const DELIVERY_PILL_CLASSES: Record<SupportDeliveryState, string> = {
  delivered: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
  bounced: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  complained: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
  unsent: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
};

/** The status filter's options; `''` is "any status" (the API drops an empty value). */
export const STATUS_FILTER_OPTIONS: readonly { value: ThreadStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed', label: 'Closed' },
  { value: 'spam', label: 'Spam' },
];

/** The statuses an operator can set from the thread view (`PATCH … { status }`). */
export const STATUS_CHOICES: readonly ThreadStatus[] = ['open', 'pending', 'closed', 'spam'];

/**
 * The statuses the select OFFERS for one thread. A machine thread (a bounce,
 * an auto-reply, the probe) is never reopened: the server answers `open` or
 * `pending` with 409 `support_patch_machine_thread`, so the page does not
 * offer a choice it knows will be refused. The thread's CURRENT status is
 * always in the list, so the select can show the value it holds.
 */
export function statusChoicesFor(thread: Pick<SupportThreadRow, 'is_machine' | 'status'> | null): readonly ThreadStatus[] {
  if (!thread) return STATUS_CHOICES;
  if (!thread.is_machine) return STATUS_CHOICES;
  const allowed: ThreadStatus[] = ['closed', 'spam'];
  return allowed.includes(thread.status) ? allowed : [thread.status, ...allowed];
}

export interface VerdictChip {
  key: keyof SupportVerdicts;
  label: string;
  tone: 'fail' | 'gray';
}

/**
 * One chip per verdict that is NOT a pass. `PASS` is the quiet state and gets
 * no chip; `FAIL`, `GRAY` and `PROCESSING_FAILED` are said, because design
 * section 4.4 keeps GRAY and PROCESSING_FAILED in the inbox "with chips" — a
 * verdict SES could not reach must never read as a pass.
 */
export function verdictChips(v: SupportVerdicts | null | undefined): VerdictChip[] {
  if (!v) return [];
  const out: VerdictChip[] = [];
  for (const key of ['spam', 'virus', 'spf', 'dkim', 'dmarc'] as const) {
    const value = v[key];
    if (!value || value === 'PASS') continue;
    out.push({
      key,
      label: `${key} ${value}`,
      tone: value === 'FAIL' ? 'fail' : 'gray',
    });
  }
  return out;
}

export const VERDICT_CHIP_CLASSES: Record<VerdictChip['tone'], string> = {
  fail: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export const SENDER_NOT_VERIFIED_CHIP = 'sender not verified';

/** The sentence beside a message that also carried HTML (design section 6.4). */
export const HTML_PRESENT_LINE = 'This message also has an HTML version — download the original';

/**
 * The reply route's refusal when the thread changed after the composer was
 * rendered (round 2, B5). The page shows the server's sentence with a Refresh
 * action and re-enables the composer after the refresh.
 */
export const THREAD_MOVED_CODE = 'support_thread_moved';

export const COPY = {
  title: 'Support Inbox',
  subtitle:
    'Mail to the company mailboxes, answered from here. Bodies are shown as text only.',
  empty: 'No threads match these filters.',
  accessDenied: 'You do not have permission to read the support inbox.',
  notFound: 'This thread does not exist, or it is in a mailbox you cannot read.',
  assignedToMe: 'Assigned to me',
  searchPlaceholder: 'Search subject, sender or text',
  machineThread:
    'A machine thread — a bounce, an auto-reply or the daily probe. It cannot be answered.',
  /** The machine → human control (round 2, B11); shown to write holders on a machine thread. */
  reopenMachine: 'Not machine mail — reopen',
  noReplyTarget: 'No human inbound message to answer.',
  textTruncated: 'The text was cut at 256 KB; the original holds the rest.',
  parseError: 'This message could not be fully parsed. The original is available.',
  /** The Refresh action beside a `support_thread_moved` refusal. */
  refresh: 'Refresh',
  deleteConfirm:
    'Erase this thread, every message and attachment in it, and the stored originals? This cannot be undone.',
  deleteControl: 'Erase thread',
  deleteConfirmControl: 'Yes, erase it',
  deleteCancel: 'Keep it',
} as const;

/**
 * The badge beside the title (round 2, B6): `N waiting > 24 h` when the
 * summary's gauge is above zero, nothing otherwise — a zero is not news.
 */
export function unansweredBadge(summary: Pick<SupportInboxSummaryData, 'unanswered_24h'> | null): string | null {
  const n = summary?.unanswered_24h;
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  return `${n} waiting > 24 h`;
}

/**
 * The restricted twin's badge (design section 11): `N restricted waiting > 24 h`
 * when the server sent a count above zero. The server sends the count to key
 * holders only and null to everyone else, so a null — like a zero — shows
 * nothing, and the page never has to ask who holds the key.
 */
export function unansweredRestrictedBadge(
  summary: Pick<SupportInboxSummaryData, 'unanswered_restricted_24h'> | null,
): string | null {
  const n = summary?.unanswered_restricted_24h;
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  return `${n} restricted waiting > 24 h`;
}

/**
 * The ONE line an own copy renders as (round 2, B2): our reply, received back
 * through the mailbox it was sent from. Never a card, never a reply target.
 */
export function ownCopyLine(
  message: Pick<SupportMessageRow, 'occurred_at'>,
  thread: Pick<SupportThreadRow, 'mailbox'>,
): string {
  return `Our reply was received back through ${thread.mailbox}@ (copy) · ${formatWhen(message.occurred_at)}`;
}

export const PAGE_SIZE = 25;

/**
 * The search box waits this long after the last keystroke before the list is
 * read. Every list read is two SQL queries and one audit row
 * (`list_support_threads`), so a read per keystroke would write one audit row
 * per letter typed.
 */
export const SEARCH_DEBOUNCE_MS = 300;

/* ═════════════════════════════════════════════════════════════════════════
 * Formatting
 * ═════════════════════════════════════════════════════════════════════════ */

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** The mailbox address a thread arrived at — `<local part>@<inbound domain>`. */
export function mailboxAddress(thread: Pick<SupportThreadRow, 'mailbox' | 'inbound_domain'>): string {
  return `${thread.mailbox}@${thread.inbound_domain}`;
}

/** "Name <address>" when a name is known, else the address alone. */
export function senderLabel(address: string, name: string | null | undefined): string {
  const trimmed = name?.trim();
  return trimmed ? `${trimmed} <${address}>` : address;
}

/* ═════════════════════════════════════════════════════════════════════════
 * The list query
 * ═════════════════════════════════════════════════════════════════════════ */

export interface ThreadListQuery {
  group: GroupTab;
  status: ThreadStatus | '';
  /** `true` sends `assigned=me`; the other filters the API accepts are not on this page. */
  assignedToMe: boolean;
  q: string;
  offset: number;
  limit: number;
  sortField: string | null;
  sortOrder: 'asc' | 'desc';
}

/**
 * The query string, built from the page state. An empty filter is OMITTED,
 * not sent as `""` — the route drops empty strings anyway, but a URL that
 * carries only what it means is easier to read in the audit row and the logs.
 * The sort is sent only when a column is active; the server default
 * (`last_message_at DESC`) applies otherwise.
 */
export function buildThreadListQuery(s: ThreadListQuery): string {
  const params = new URLSearchParams();
  // A people tab is one mailbox, read by exact local part; every other tab is a group.
  if (isPeopleTab(s.group)) params.set('mailbox', s.group);
  else if (s.group !== 'all') params.set('group', s.group);
  if (s.status) params.set('status', s.status);
  if (s.assignedToMe) params.set('assigned', 'me');
  const q = s.q.trim();
  if (q) params.set('q', q.slice(0, 200));
  params.set('limit', String(s.limit));
  params.set('offset', String(s.offset));
  if (s.sortField) {
    params.set('sort_by', s.sortField);
    params.set('sort_order', s.sortOrder);
  }
  return params.toString();
}

/* ═════════════════════════════════════════════════════════════════════════
 * Reads and writes — every call goes through adminFetch
 * ═════════════════════════════════════════════════════════════════════════ */

/** The wire shape of a refusal: RFC 9457 problem details, as `createErrorResponse` writes them. */
interface ProblemBody {
  title?: unknown;
  detail?: unknown;
  code?: unknown;
  message?: unknown;
}

function problemBody(e: unknown): ProblemBody | null {
  if (!(e instanceof AdminApiError)) return null;
  const b = e.body;
  return b && typeof b === 'object' ? (b as ProblemBody) : null;
}

/**
 * The server's own words, read from the WIRE SHAPE. Every API refusal is an
 * RFC 9457 body — `{ type, title, status, code?, detail? }` from
 * `backend/src/utils/api-helpers.ts:createErrorResponse` — and the sentence
 * (`ConflictError`'s message: a 409 `support_reply_machine_thread`, a 429
 * rate cap, the non-prod gate) is `title`. There is NO `message` member, so
 * `adminFetch`'s own `AdminApiError.message` is only ever `HTTP <status>`:
 * reading it would show the operator a number, never the reason (stage D
 * review, 2026-09-26). `title` first; `detail` appended when it is a
 * sentence (an `AppError` puts a JSON object there, which is not for a
 * toast); `message` for any non-RFC body; the transport message last.
 */
export function errorMessage(e: unknown): string {
  const b = problemBody(e);
  if (b) {
    const title = typeof b.title === 'string' ? b.title.trim() : '';
    const detail = typeof b.detail === 'string' ? b.detail.trim() : '';
    const detailIsSentence = detail.length > 0 && !/^[[{]/.test(detail);
    if (title && detailIsSentence && detail !== title) return `${title} — ${detail}`;
    if (title) return title;
    if (detailIsSentence) return detail;
    if (typeof b.message === 'string' && b.message.trim()) return b.message.trim();
  }
  const message = (e as Error)?.message;
  return message && message.length > 0 ? message : 'The request failed.';
}

/** The stable machine code (`body.code`, snake_case) a refusal carries, or null. */
export function errorCode(e: unknown): string | null {
  const b = problemBody(e);
  return b && typeof b.code === 'string' && b.code.length > 0 ? b.code : null;
}

export function errorStatus(e: unknown): number | null {
  return e instanceof AdminApiError ? e.status : null;
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as { ok?: boolean; data?: T };
  if (!body || body.ok !== true || body.data === undefined || body.data === null) {
    throw new Error('The response carried no data.');
  }
  return body.data;
}

export async function fetchSummary(apiUrl: string): Promise<SupportInboxSummaryData> {
  return unwrap<SupportInboxSummaryData>(await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.summary}`));
}

export async function fetchThreads(apiUrl: string, query: ThreadListQuery): Promise<SupportThreadListData> {
  return unwrap<SupportThreadListData>(
    await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.threads}?${buildThreadListQuery(query)}`),
  );
}

export async function fetchThread(apiUrl: string, threadId: string): Promise<SupportThreadData> {
  return unwrap<SupportThreadData>(await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.thread(threadId)}`));
}

/**
 * POST the reply. `expectedLastMessageAt` is the thread's `last_message_at`
 * the composer rendered (round 2, B5): the route refuses with 409
 * `support_thread_moved` when the thread changed since. Omitted only when the
 * page has no thread to read it from, which cannot happen on a rendered
 * composer — the field is optional on the wire for one deploy's compatibility.
 */
export async function postReply(
  apiUrl: string,
  threadId: string,
  text: string,
  expectedLastMessageAt?: string | null,
): Promise<SupportReplyData> {
  return unwrap<SupportReplyData>(
    await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.reply(threadId)}`, {
      method: 'POST',
      body: JSON.stringify({
        text,
        ...(expectedLastMessageAt ? { expected_last_message_at: expectedLastMessageAt } : {}),
      }),
    }),
  );
}

export interface ThreadPatch {
  status?: ThreadStatus;
  assigned_admin_id?: string | null;
  /** Only `false` is a request: machine → human (round 2, B11). The route refuses `true`. */
  is_machine?: false;
}

export async function patchThread(apiUrl: string, threadId: string, patch: ThreadPatch): Promise<SupportThreadRow> {
  return unwrap<SupportThreadRow>(
    await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.thread(threadId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  );
}

export async function deleteThreadRequest(apiUrl: string, threadId: string): Promise<SupportThreadErasedData> {
  return unwrap<SupportThreadErasedData>(
    await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.thread(threadId)}`, { method: 'DELETE' }),
  );
}

export async function fetchAttachmentDownload(apiUrl: string, attachmentId: string): Promise<SupportDownloadData> {
  return unwrap<SupportDownloadData>(
    await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.attachmentDownload(attachmentId)}`),
  );
}

export async function fetchRawDownload(apiUrl: string, messageId: string): Promise<SupportDownloadData> {
  return unwrap<SupportDownloadData>(await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.raw(messageId)}`));
}

/** `GET /v1/admin/admins` answers a bare array, not the `{ok, data}` envelope. */
export async function fetchAdmins(apiUrl: string): Promise<AdminListRow[]> {
  const res = await adminFetch(`${apiUrl}${SUPPORT_INBOX_PATHS.admins}`);
  const rows = (await res.json()) as unknown;
  if (!Array.isArray(rows)) throw new Error('The admins list was not an array.');
  return rows
    .filter((r): r is AdminListRow => !!r && typeof r === 'object' && typeof (r as AdminListRow).id === 'string')
    .map((r) => ({ id: r.id, email: r.email, display_name: r.display_name, is_active: r.is_active }));
}

/**
 * A presigned URL is opened in a NEW tab with `noopener`, never navigated to
 * in place and never rendered: the raw `.eml` and an attachment are served
 * `application/octet-stream` with a `Content-Disposition: attachment`, so the
 * browser saves them; the panel's page never hosts their bytes.
 */
export function openInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener');
}

/* ═════════════════════════════════════════════════════════════════════════
 * Attachments — the download control's ONE rule
 * ═════════════════════════════════════════════════════════════════════════ */

export interface AttachmentView extends SupportAttachmentRow {
  /** The download control exists only when this is true. */
  downloadAllowed: boolean;
}

/**
 * `download_allowed` is the API's word (virus PASS AND bytes stored). The page
 * re-checks the message's own virus verdict beside it, so a download control
 * can never appear for an attachment whose message did not PASS — even if an
 * older or newer API disagreed with itself. Both must hold.
 */
export function attachmentViews(
  attachments: SupportAttachmentRow[],
  messages: SupportMessageRow[],
): AttachmentView[] {
  const virusByMessage = new Map<string, string | null>();
  for (const m of messages) virusByMessage.set(m.id, m.verdicts?.virus ?? null);
  return attachments.map((a) => ({
    ...a,
    downloadAllowed: a.download_allowed === true && virusByMessage.get(a.message_id) === 'PASS',
  }));
}

/**
 * The outbound row the thread view appends after a successful reply, built
 * from what the API returned and what was sent. `delivery` starts at `sent`:
 * SES has accepted the message and no event has arrived yet — the same word
 * the thread read would give it.
 */
export function outboundRowFromReply(
  reply: SupportReplyData,
  thread: SupportThreadRow,
  text: string,
  adminId: string | null,
  now: Date = new Date(),
): SupportMessageRow {
  return {
    id: reply.message_id,
    direction: 'outbound',
    ses_message_id: reply.ses_message_id,
    rfc_message_id: null,
    in_reply_to: null,
    references_ids: [],
    envelope_from: null,
    from_address: mailboxAddress(thread),
    from_name: 'Abby Support',
    to_addresses: [reply.to],
    cc_addresses: [],
    subject: thread.subject ? `Re: ${thread.subject}` : null,
    text_body: text,
    text_truncated: false,
    html_present: false,
    parse_error: null,
    has_raw: false,
    verdicts: { spam: null, virus: null, spf: null, dkim: null, dmarc: null },
    headers_truncated: false,
    is_machine: false,
    sent_by_admin_id: adminId,
    occurred_at: now.toISOString(),
    delivery: 'sent',
    own_copy: false,
  };
}
